/**
 * Video Carousel Manager Custom Element
 * Manages video carousel functionality including playback, modes (none/popup/modal), and Splide integration
 */
class VideoCarouselManager extends HTMLElement {
  constructor() {
    super();
    this.splide = null;
    this.allVideos = [];
    this.allPlayOverlays = [];
    this.currentPlaying = null;
    this.videoIndex = 0;
    this.videoPlayTime = 5000;
    this.playTimer = null;
    this.modalInstance = null;
    this.popupInstance = null;
  }

  connectedCallback() {
    // Wait for Splide to be available
    if (typeof Splide === 'undefined') {
      console.error('Splide library not loaded');
      return;
    }

    this.initCarousel();
  }

  disconnectedCallback() {
    // Cleanup
    if (this.playTimer) {
      clearTimeout(this.playTimer);
    }
    if (this.splide) {
      this.splide.destroy();
    }
    if (this.modalInstance?.destroy) {
      this.modalInstance.destroy();
    }
  }

  // Get configuration from data attributes
  getConfig() {
    return {
      sectionId: this.dataset.sectionId,
      mode: this.dataset.mode,
      carouselAutoplay: this.dataset.carouselAutoplay === 'true',
      videoAutoplay: this.dataset.videoAutoplay === 'true',
      interval: parseInt(this.dataset.interval, 10) || 5000,
      loop: this.dataset.loop === 'true',
      pauseOnHover: this.dataset.pauseOnHover === 'true',
      itemsDesktop: parseInt(this.dataset.itemsDesktop, 10) || 3,
      itemsMobile: parseInt(this.dataset.itemsMobile, 10) || 1,
      gap: this.dataset.gap || '16px',
      gapMobile: this.dataset.gapMobile || '8px',
      circleGap: this.dataset.circleGap || '16px',
      circleGapMobile: this.dataset.circleGapMobile || '8px',
      circleLayout: this.dataset.circleLayout === 'true',
      hideArrows: this.dataset.hideArrows === 'true',
      playbackStrategy: this.dataset.playbackStrategy || 'sequential'
    };
  }

  initCarousel() {
    const config = this.getConfig();
    const carouselElement = document.getElementById(`splide-${config.sectionId}`);
    
    if (!carouselElement) {
      console.error('Carousel element not found');
      return;
    }

    this.carouselElement = carouselElement;
    const isCircleLayout = config.circleLayout;

    // Initialize Splide
    this.splide = new Splide(`#splide-${config.sectionId}`, {
      type: 'slide',
      rewind: config.loop,
      perPage: config.itemsDesktop,
      perMove: 1,
      gap: isCircleLayout ? config.circleGap : config.gap,
      pagination: false,
      arrows: !config.hideArrows,
      autoplay: config.carouselAutoplay,
      interval: config.interval,
      pauseOnHover: config.pauseOnHover,
      pauseOnFocus: config.pauseOnHover,
      lazyLoad: 'nearby',
      updateOnMove: true,
      breakpoints: {
        749: {
          perPage: config.itemsMobile,
          gap: isCircleLayout ? config.circleGapMobile : config.gapMobile
        }
      }
    });

    // Get all videos and play overlays
    this.allVideos = Array.from(carouselElement.querySelectorAll('.video-slide__video'));
    this.allPlayOverlays = Array.from(carouselElement.querySelectorAll('.video-slide__play-overlay'));

    // Mount Splide and initialize video playback
    this.splide.on('mounted', () => {
      carouselElement.classList.add('is-initialized');
      this.updateArrows();

      // Different behavior based on playback strategy
      if (config.playbackStrategy === 'simultaneous') {
        if (config.videoAutoplay) {
          this.playAllVideos();
        }
      } else {
        // For sequential playback, show all play overlays initially
        this.allPlayOverlays.forEach(overlay => {
          overlay.style.opacity = '1';
        });

        // Start the sequence after a delay
        if (this.playTimer) clearTimeout(this.playTimer);
        if (config.videoAutoplay) {
          this.playTimer = setTimeout(() => this.playNextVideo(), 500);
        }
      }

      // Initialize modal or popup based on mode
      if (config.mode === 'modal') {
        this.initModal();
      } else if (config.mode === 'popup') {
        this.initPopupMode();
      }
    });

    // Update arrows when the slide changes
    this.splide.on('moved', () => {
      this.updateArrows();

      const config = this.getConfig();


      // Only do sequential playback if strategy is sequential
      if (config.playbackStrategy === 'sequential') {
        const activeSlide = this.splide.index;

        // Stop current playing
        if (this.currentPlaying) {
          this.currentPlaying.pause();
          this.currentPlaying.currentTime = 0;
        }

        // Set the index to the active slide
        this.videoIndex = activeSlide;

        // Clear any existing timeout
        if (this.playTimer) {
          clearTimeout(this.playTimer);
          this.playTimer = null;
        }

        // Restart the sequence
        if (config.videoAutoplay) {
          this.playTimer = setTimeout(() => this.playNextVideo(), 100);
        }
      }
    });

    this.splide.mount();
  }

  // Play all videos simultaneously (for simultaneous strategy)
  playAllVideos() {
    const config = this.getConfig();
    
    this.allVideos.forEach((video, index) => {
      // Set video to loop for infinite autoplay
      if (config.videoAutoplay && config.loop) {
        video.loop = true;
      }

      // Play the video
      video.play().catch(error => {
        console.log('Video play failed:', error);
      });

      // Hide all play overlays
      if (this.allPlayOverlays[index]) {
        this.allPlayOverlays[index].style.opacity = '0';
      }

      // Add event listener to restart video if autoplay and loop are enabled
      if (config.videoAutoplay && config.loop) {
        video.addEventListener('ended', function restartVideo() {
          video.currentTime = 0;
          video.play().catch(error => {
            console.log('Video restart failed:', error);
          });
        });
      }
    });
  }

  // Play videos in sequence (for sequential strategy)
  playNextVideo() {
    // Clear any existing timeout to prevent stacking
    if (this.playTimer) {
      clearTimeout(this.playTimer);
      this.playTimer = null;
    }

    // Stop the current video if there is one
    if (this.currentPlaying) {
      this.currentPlaying.pause();
      this.currentPlaying.currentTime = 0;
    }

    // Move to next video or reset to the first one
    if (this.videoIndex >= this.allVideos.length) {
      this.videoIndex = 0;
    }

    // Get the next video
    this.currentPlaying = this.allVideos[this.videoIndex];

    // Play the video
    if (this.currentPlaying) {
      this.currentPlaying.play().catch(error => {
        console.log('Video play failed:', error);
        // Try next video if this one fails
        this.videoIndex++;

        if (this.playTimer) clearTimeout(this.playTimer);
        this.playTimer = setTimeout(() => this.playNextVideo(), 100);
        return;
      });

      // Update play button overlays
      this.updatePlayOverlays();

      // Move to the slide that contains this video
      this.splide.go(this.videoIndex);

      // Move to the next video
      this.videoIndex++;

      // Set the next timeout and store the reference
      if (this.playTimer) clearTimeout(this.playTimer);
      this.playTimer = setTimeout(() => this.playNextVideo(), this.videoPlayTime);
    }
  }

  // Update play overlays (hide for current video, show for others)
  updatePlayOverlays() {
    this.allPlayOverlays.forEach((overlay, index) => {
      if (index === this.videoIndex) {
        overlay.style.opacity = '0';
      } else {
        overlay.style.opacity = '1';
      }
    });
  }

  // Update arrow visibility
  updateArrows() {
    const config = this.getConfig();
    const prevArrow = this.carouselElement.querySelector('.splide__arrow--prev');
    const nextArrow = this.carouselElement.querySelector('.splide__arrow--next');

    if (!prevArrow || !nextArrow) return;

    // If there's only one page to show, hide both arrows
    if (this.splide.length <= this.splide.options.perPage) {
      prevArrow.style.display = 'none';
      nextArrow.style.display = 'none';
      return;
    }

    // If not looping, hide the prev arrow when at the beginning
    if (!config.loop && this.splide.index === 0) {
      prevArrow.style.display = 'none';
    } else {
      prevArrow.style.display = '';
    }

    // If not looping, hide the next arrow when at the end
    const isLastPage = this.splide.index >= this.splide.length - this.splide.options.perPage;

    if (!config.loop && isLastPage) {
      nextArrow.style.display = 'none';
    } else {
      nextArrow.style.display = '';
    }
  }

  // Initialize modal mode
  initModal() {
    const config = this.getConfig();
    const modal = document.getElementById(`video-modal-${config.sectionId}`);
    
    if (!modal) return;

    // Move modal to body to avoid stacking context issues
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    // Check if already initialized
    if (modal.dataset.initialized === 'true') {
      return;
    }
    modal.dataset.initialized = 'true';

    const modalClose = modal.querySelector('.video-modal__close');
    const modalMute = modal.querySelector('.video-modal__mute');
    const mutedIcon = modalMute.querySelector('.video-modal__mute-icon--muted');
    const unmutedIcon = modalMute.querySelector('.video-modal__mute-icon--unmuted');
    const videos = modal.querySelectorAll('.video-modal__video');
    const playPauseButtons = modal.querySelectorAll('.video-modal__play-pause');
    const videoContainers = modal.querySelectorAll('.video-modal__video-container');

    let isMuted = false;

    // Initialize the modal Splide
    const modalSplide = new Splide(`#modal-splide-${config.sectionId}`, {
      type: 'slide',
      direction: 'ttb',
      height: '100vh',
      arrows: !config.hideArrows,
      pagination: false,
      perPage: 1,
      perMove: 1,
      gap: 0,
      speed: 400,
      easing: 'ease',
      dragMinThreshold: 40,
      waitForTransition: true,
      breakpoints: {
        749: {
          height: '100vh'
        }
      }
    });

    modalSplide.mount();

    // Video control functions
    const playCurrentVideo = () => {
      const currentIndex = modalSplide.index;
      if (videos[currentIndex]) {
        const video = videos[currentIndex];
        const playButton = playPauseButtons[currentIndex].querySelector('.video-modal__play-icon');
        const pauseButton = playPauseButtons[currentIndex].querySelector('.video-modal__pause-icon');

        video.muted = isMuted;
        
        if (video.readyState >= 2) {
          video.play().catch(error => console.log('Could not play video:', error));
        } else {
          video.addEventListener('canplay', function playWhenReady() {
            video.play().catch(error => console.log('Could not play video:', error));
            video.removeEventListener('canplay', playWhenReady);
          }, { once: true });
        }

        if (playButton) playButton.style.display = 'none';
        if (pauseButton) pauseButton.style.display = 'block';
      }
    };

    const pauseCurrentVideo = () => {
      const currentIndex = modalSplide.index;
      if (videos[currentIndex]) {
        const video = videos[currentIndex];
        const playButton = playPauseButtons[currentIndex].querySelector('.video-modal__play-icon');
        const pauseButton = playPauseButtons[currentIndex].querySelector('.video-modal__pause-icon');

        video.pause();
        if (playButton) playButton.style.display = 'block';
        if (pauseButton) pauseButton.style.display = 'none';
      }
    };

    const toggleCurrentVideo = () => {
      const currentIndex = modalSplide.index;
      if (videos[currentIndex]) {
        const video = videos[currentIndex];
        if (video.paused) {
          playCurrentVideo();
        } else {
          pauseCurrentVideo();
        }
      }
    };

    const pauseAllVideos = () => {
      videos.forEach((video, index) => {
        video.pause();

        const playButton = playPauseButtons[index].querySelector('.video-modal__play-icon');
        const pauseButton = playPauseButtons[index].querySelector('.video-modal__pause-icon');

        if (playButton) playButton.style.display = 'block';
        if (pauseButton) pauseButton.style.display = 'none';
      });
    };

    // Make video containers clickable
    videoContainers.forEach(container => {
      container.addEventListener('click', e => {
        if (e.target === container || e.target.classList.contains('video-modal__video')) {
          e.preventDefault();
          e.stopPropagation();
          toggleCurrentVideo();
        }
      });
    });

    // Play/pause buttons
    playPauseButtons.forEach((button, index) => {
      button.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();

        const video = videos[index];
        const playButton = button.querySelector('.video-modal__play-icon');
        const pauseButton = button.querySelector('.video-modal__pause-icon');

        if (video.paused) {
          video.play();
          playButton.style.display = 'none';
          pauseButton.style.display = 'block';
        } else {
          video.pause();
          playButton.style.display = 'block';
          pauseButton.style.display = 'none';
        }
      });
    });

    // Handle slide changes
    modalSplide.on('moved', () => {
      pauseAllVideos();
      playCurrentVideo();
    });

    // Mute button
    modalMute.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();

      isMuted = !isMuted;

      videos.forEach(video => {
        video.muted = isMuted;
      });

      if (isMuted) {
        mutedIcon.style.display = 'block';
        unmutedIcon.style.display = 'none';
      } else {
        mutedIcon.style.display = 'none';
        unmutedIcon.style.display = 'block';
      }
    });

    const closeModal = () => {
      modal.classList.remove('active');
      document.body.classList.remove('modal-open');
      pauseAllVideos();

      const scrollY = parseInt(modal.getAttribute('data-scroll-position') || '0', 10);
      setTimeout(() => {
        window.scrollTo(0, scrollY);
      }, 50);

      document.removeEventListener('keydown', handleEscKey);
    };

    const openModal = (startIndex = 0) => {
      const scrollY = window.scrollY;
      modal.setAttribute('data-scroll-position', scrollY);

      modal.classList.add('active');
      document.body.classList.add('modal-open');

      modalSplide.go(startIndex);

      isMuted = false;
      mutedIcon.style.display = 'none';
      unmutedIcon.style.display = 'block';

      setTimeout(() => {
        playCurrentVideo();
      }, 100);

      document.addEventListener('keydown', handleEscKey);
    };

    const handleEscKey = e => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };

    // Modal controls
    modalClose.addEventListener('click', closeModal);
    modal.addEventListener('click', closeModal);
    modal.querySelector('.video-modal__splide').addEventListener('click', e => {
      if (e.target === modal.querySelector('.video-modal__splide')) {
        e.stopPropagation();
      }
    });

    // Make carousel slides clickable
    const carouselSlides = document.querySelectorAll(`#splide-${config.sectionId} .splide__slide`);
    carouselSlides.forEach((slide, index) => {
      slide.addEventListener('click', () => {
        openModal(index);
      });
    });

    this.modalInstance = {
      open: openModal,
      close: closeModal,
      destroy: () => {
        document.removeEventListener('keydown', handleEscKey);
        modal.dataset.initialized = 'false';
        // Move back to custom element to ensure cleanup
        if (modal.parentElement === document.body) {
          this.appendChild(modal);
        }
      }
    };
  }

  // Initialize popup mode
  initPopupMode() {
    const config = this.getConfig();
    
    if (config.mode !== 'popup') return;

    const carouselSlides = this.querySelectorAll(`#splide-${config.sectionId} .splide__slide`);
    const popups = this.querySelectorAll('.video-carousel-popup');

    if (popups.length === 0) return;

    let activePopup = null;
    const popupVideos = {};

    // Initialize each popup
    popups.forEach(popup => {
      const popupId = popup.id;
      const video = popup.querySelector('.video-carousel-popup__video');
      const closeButton = popup.querySelector('.video-carousel-popup__close');
      const playPauseButton = popup.querySelector('.video-carousel-popup__play-pause');
      const playIcon = playPauseButton.querySelector('.video-carousel-popup__play-icon');
      const pauseIcon = playPauseButton.querySelector('.video-carousel-popup__pause-icon');
      const videoWrapper = popup.querySelector('.video-carousel-popup__video-wrapper');

      popupVideos[popupId] = video;

      // Close button
      closeButton.addEventListener('click', e => {
        e.stopPropagation();
        popup.classList.remove('active');
        // Wait for transition to finish before hiding
        setTimeout(() => {
            popup.style.display = 'none';
        }, 500);

        if (video) {
          video.pause();
          video.currentTime = 0;
        }
        activePopup = null;
      });

      // Video wrapper click to toggle play/pause
      videoWrapper.addEventListener('click', () => {
        if (!video) return;
        
        if (video.paused) {
          video.play();
          playIcon.style.display = 'none';
          pauseIcon.style.display = 'block';
        } else {
          video.pause();
          playIcon.style.display = 'block';
          pauseIcon.style.display = 'none';
        }
      });

      // Play/pause button
      playPauseButton.addEventListener('click', e => {
        e.stopPropagation();
        if (!video) return;

        if (video.paused) {
          video.play();
          playIcon.style.display = 'none';
          pauseIcon.style.display = 'block';
        } else {
          video.pause();
          playIcon.style.display = 'block';
          pauseIcon.style.display = 'none';
        }
      });
    });

    // Make carousel slides clickable to open popups
    carouselSlides.forEach((slide, index) => {
      slide.addEventListener('click', () => {
        // Close any active popup
        if (activePopup) {
          activePopup.classList.remove('active');
          activePopup.style.display = 'none'; // Hide immediately if switching
          const activeVideo = popupVideos[activePopup.id];
          if (activeVideo) {
            activeVideo.pause();
            activeVideo.currentTime = 0;
          }
        }

        // Open the corresponding popup
        if (popups[index]) {
          const popup = popups[index];
          popup.style.display = 'block';
          // Small delay to allow display:block to apply before adding active class for transition
          requestAnimationFrame(() => {
            popup.classList.add('active');
          });
          
          activePopup = popup;

          // Play the video
          const video = popupVideos[popup.id];
          if (video) {
            video.play().catch(error => {
              console.log('Video play failed:', error);
            });

            const playPauseButton = popup.querySelector('.video-carousel-popup__play-pause');
            const playIcon = playPauseButton.querySelector('.video-carousel-popup__play-icon');
            const pauseIcon = playPauseButton.querySelector('.video-carousel-popup__pause-icon');

            playIcon.style.display = 'none';
            pauseIcon.style.display = 'block';
          }
        }
      });
    });

    this.popupInstance = {
      closeAll: () => {
        popups.forEach(popup => {
          popup.classList.remove('active');
          const video = popupVideos[popup.id];
          if (video) {
            video.pause();
            video.currentTime = 0;
          }
        });
        activePopup = null;
      }
    };
  }
}

// Register the custom element
customElements.define('video-carousel-manager', VideoCarouselManager);
